import CatalogSideNavIcon from '@assets/icons/catalog-side-nav.svg';
import ChatsSideNavIcon from '@assets/icons/chats-side-nav.svg';
import EmployeesSideNavIcon from '@assets/icons/employees-side-nav.svg';
import ExternalContentSideNavIcon from '@assets/icons/chain-link.svg';
import i18n from '@/utils/i18n';
import MenuSideNavIcon from '@assets/icons/menu-side-nav.svg';
import MoreSideNavIcon from '@assets/icons/menu-horizontal.svg';
import OrderGuideSideNavIcon from '@assets/icons/order-guide-side-nav.svg';
import OrdersSideNavIcon from '@assets/icons/order-side-nav.svg';
import PaymentsSideNavIcon from '@assets/icons/payments-side-nav.svg';
import React from 'react';
import {
  ActiveNavEntry,
  RouteMapType,
  useSideNavPress,
} from '@/hooks/use-side-nav-press';
import { APP_ROUTES } from '@/navigators/routes';
import {
  getMicroProductIconConfig,
  PROMOTED_MICROPRODUCT_ICON_SIZE,
} from '@/screens/storefront-microproducts/micro-product-icon-config';
import { MenuItemIcon } from '@/components/menu-item-icon';
import { MicroProduct } from '@domain/microproducts/loaders/use-microproducts-loader';
import { MORE_MENU_ABSORBED_ROUTES } from './more-menu/more-menu-absorbed-routes';
import { NAVIGATION_TAB_LABELS } from '@/utils/navigation';
import { SideNavButton } from './side-nav-button';
import { SvgProps } from 'react-native-svg';
import { useTheme } from '@/contexts/theme-context';
import { View } from 'react-native';

interface Props {
  activeEntry: ActiveNavEntry;
  moreMenuActionSheetRef?: React.RefObject<View | null>;
  onClose: () => void;
  onStorefrontProductPress?: (productId: string) => void;
  promotedProducts?: MicroProduct[];
  routes: RouteMapType[];
  setIsMoreMenuSheetOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  showMoreMenu?: boolean;
}

// Partial on purpose: routes without an icon entry render no rail tile
// (e.g. the hidden StorefrontMicroProduct tab, which has no direct entry).
const TAB_ICONS: Partial<Record<APP_ROUTES, React.FC<SvgProps>>> = {
  [APP_ROUTES.Account]: EmployeesSideNavIcon,
  [APP_ROUTES.Catalog]: CatalogSideNavIcon,
  [APP_ROUTES.Chat]: ChatsSideNavIcon,
  [APP_ROUTES.Chats]: ChatsSideNavIcon,
  [APP_ROUTES.Links]: ExternalContentSideNavIcon,
  [APP_ROUTES.MenuInfo]: MenuSideNavIcon,
  [APP_ROUTES.OrderGuide]: OrderGuideSideNavIcon,
  [APP_ROUTES.Orders]: OrdersSideNavIcon,
  [APP_ROUTES.Payments]: PaymentsSideNavIcon,
  [APP_ROUTES.Restaurant]: EmployeesSideNavIcon,
};

export const SideNavItemList: React.FC<Props> = ({
  activeEntry,
  moreMenuActionSheetRef,
  onClose,
  onStorefrontProductPress,
  promotedProducts,
  routes,
  setIsMoreMenuSheetOpen,
  showMoreMenu,
}) => {
  const theme = useTheme();
  const onSideNavPress = useSideNavPress();
  const moreFocused = activeEntry.type === 'more';
  const moreIconColor = moreFocused
    ? theme.colors.primary
    : theme.colors.accentText;

  return (
    <>
      {routes.map(({ route, index }) => {
        const focused =
          activeEntry.type === 'route' && activeEntry.index === index;
        const IconComponent = TAB_ICONS[route.name as APP_ROUTES];
        if (!IconComponent) {
          return null;
        }

        // No tile for routes absorbed into the More menu popup (external
        // content links, storefront microproducts) — see drawer-content.tsx.
        if (MORE_MENU_ABSORBED_ROUTES.has(route.name)) {
          return null;
        }

        const routeTitle = NAVIGATION_TAB_LABELS[route.name as APP_ROUTES];
        const iconColor = focused
          ? theme.colors.primary
          : theme.colors.accentText;

        return (
          <View key={route.key}>
            <SideNavButton
              focused={focused}
              icon={<IconComponent fill={iconColor} stroke={iconColor} />}
              onPress={() => {
                onSideNavPress(route.name as APP_ROUTES, focused);
                onClose();
              }}
              testID={`${route.name}-icon`}
              title={routeTitle ? i18n.t(routeTitle) : undefined}
            />
          </View>
        );
      })}
      {promotedProducts?.map((product) => {
        const focused =
          activeEntry.type === 'product' &&
          activeEntry.productId === product.id;
        const iconColor = focused
          ? theme.colors.primary
          : theme.colors.accentText;

        return (
          <View key={product.id}>
            <SideNavButton
              focused={focused}
              icon={
                <MenuItemIcon
                  color={iconColor}
                  item={getMicroProductIconConfig(product)}
                  size={PROMOTED_MICROPRODUCT_ICON_SIZE}
                />
              }
              // Truncate at 2 lines to support longer product display names
              numberOfLines={2}
              onPress={() => onStorefrontProductPress?.(product.id)}
              testID={`storefront-microproduct-${product.id}`}
              title={product.displayName}
            />
          </View>
        );
      })}
      {showMoreMenu ? (
        <View ref={moreMenuActionSheetRef}>
          <SideNavButton
            focused={moreFocused}
            icon={
              <MoreSideNavIcon fill={moreIconColor} stroke={moreIconColor} />
            }
            onPress={() => setIsMoreMenuSheetOpen?.(true)}
            testID="side-nav-more"
            title={i18n.t('more')}
          />
        </View>
      ) : null}
    </>
  );
};
